/**
 * Book of Business — Phase 7G.2.
 *
 * Admin-key gated. Fetches /api/analytics/book-of-business and renders one
 * card per advisor, broken down by product line.
 *
 * Access scope: if `lwp_advisor_slug` is set in localStorage, the view is
 * filtered to that advisor's book only. Admins/compliance leave it blank
 * to see all advisors.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { Card, Header, KeyGate, Loading, ErrView, Stat } from './AdminCompliance'
import { AdminThemeToggle, useAdminTheme } from './admin/theme'
import PreProdWarnings from './admin/PreProdWarnings'

const API_URL = process.env.REACT_APP_BACKEND_URL || ''

function useAdminKey() {
  const [key, setKey] = useState(() => localStorage.getItem('lwp_admin_key') || '')
  const [input, setInput] = useState('')
  return {
    key,
    setKey: (v) => {
      localStorage.setItem('lwp_admin_key', v)
      setKey(v)
    },
    input,
    setInput,
    reset: () => {
      localStorage.removeItem('lwp_admin_key')
      setKey('')
    },
  }
}

function fmtMoney(n) {
  if (n === null || n === undefined) return '—'
  const v = Number(n) || 0
  return `$${v.toLocaleString()}`
}

function AdvisorSection({ advisor }) {
  const active = advisor.by_product_line.reduce((s, l) => s + l.policies.length, 0)
  return (
    <div
      style={{
        background: 'var(--admin-surface)',
        borderRadius: 10,
        padding: 20,
        marginBottom: 16,
        border: '1px solid var(--admin-border)',
      }}
      data-testid={`book-advisor-${advisor.advisor_slug}`}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 style={{ fontFamily: 'Fraunces,serif', color: 'var(--admin-navy)', margin: 0, fontSize: 22 }}>
            {advisor.advisor_name || advisor.advisor_slug}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--admin-muted)', marginTop: 2 }}>
            slug: <code style={{ fontFamily: 'monospace' }}>{advisor.advisor_slug}</code>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 20, fontSize: 12, color: 'var(--admin-muted)' }}>
          <div><span style={{ display: 'block', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Clients</span><span style={{ fontFamily: 'Fraunces,serif', fontSize: 20, color: 'var(--admin-navy)' }}>{advisor.total_clients}</span></div>
          <div><span style={{ display: 'block', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Active policies</span><span style={{ fontFamily: 'Fraunces,serif', fontSize: 20, color: 'var(--admin-navy)' }}>{active}</span></div>
          <div><span style={{ display: 'block', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total face</span><span style={{ fontFamily: 'Fraunces,serif', fontSize: 20, color: 'var(--admin-navy)' }}>{fmtMoney(advisor.total_active_face_amount)}</span></div>
          <div><span style={{ display: 'block', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Annualized prem.</span><span style={{ fontFamily: 'Fraunces,serif', fontSize: 20, color: 'var(--admin-gold)' }}>{fmtMoney(advisor.annualized_premium)}</span></div>
        </div>
      </div>

      {advisor.by_product_line.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--admin-muted)', padding: '12px 0' }} data-testid={`book-empty-${advisor.advisor_slug}`}>
          No active policies yet.
        </div>
      )}

      {advisor.by_product_line.map((line) => (
        <div key={line.line} style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--admin-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
            {line.line} · {line.policies.length}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--admin-scrim)' }}>
                <th style={th}>Client</th>
                <th style={th}>Product</th>
                <th style={th}>Carrier</th>
                <th style={thR}>Face</th>
                <th style={thR}>Premium</th>
                <th style={thR}>Renewal</th>
              </tr>
            </thead>
            <tbody>
              {line.policies.map((p) => (
                <tr key={p.policy_id}>
                  <td style={td}>{p.client_name}</td>
                  <td style={td}>{p.product_name || p.product_slug}</td>
                  <td style={td}>{p.carrier || '—'}</td>
                  <td style={tdR}>{fmtMoney(p.face_amount)}</td>
                  <td style={tdR}>{fmtMoney(p.premium_amount)} <span style={{ color: 'var(--admin-muted)', fontSize: 11 }}>/{(p.premium_frequency || 'annual').slice(0, 3)}</span></td>
                  <td style={tdR}>
                    {p.renewal_at ? new Date(p.renewal_at).toISOString().slice(0, 10) : '—'}
                    {typeof p.days_to_renewal === 'number' && p.days_to_renewal >= 0 && p.days_to_renewal <= 30 && (
                      <span style={{ marginLeft: 6, fontSize: 10, color: '#B45309', background: '#FEF3C7', padding: '1px 5px', borderRadius: 4 }}>
                        {p.days_to_renewal}d
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  )
}

export default function AdminBook() {
  useAdminTheme()
  const auth = useAdminKey()
  const [advisorScope, setAdvisorScope] = useState(() => localStorage.getItem('lwp_advisor_slug') || '')
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [loading, setLoading] = useState(false)

  const load = () => {
    if (!auth.key) return
    setLoading(true)
    setErr(null)
    fetch(`${API_URL}/api/analytics/book-of-business?admin_key=${encodeURIComponent(auth.key)}`)
      .then((r) => {
        if (r.status === 401) throw new Error('Invalid admin key')
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then(setData)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false))
  }
  useEffect(load, [auth.key])

  const filtered = useMemo(() => {
    if (!data) return null
    if (!advisorScope) return data.advisors
    return data.advisors.filter((a) => a.advisor_slug === advisorScope)
  }, [data, advisorScope])

  const rollup = useMemo(() => {
    const list = filtered || []
    return {
      advisors: list.length,
      clients: list.reduce((s, a) => s + (a.total_clients || 0), 0),
      face: list.reduce((s, a) => s + (a.total_active_face_amount || 0), 0),
      premium: list.reduce((s, a) => s + (a.annualized_premium || 0), 0),
    }
  }, [filtered])

  if (!auth.key) return <KeyGate auth={auth} title="Book of Business" />
  if (loading) return <Loading />
  if (err) return <ErrView err={err} reset={auth.reset} />
  if (!data) return null

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--admin-ivory)',
        fontFamily: 'Inter,-apple-system,sans-serif',
        padding: 24,
        color: 'var(--admin-slate)',
      }}
      data-testid="admin-book-view"
    >
      <PreProdWarnings apiUrl={API_URL} adminKey={auth.key} />
      <Header title="Book of Business" subtitle={advisorScope ? `Scoped to advisor: ${advisorScope}` : `${data.advisors.length} advisors · all lines of business`}>
        <input
          type="text"
          value={advisorScope}
          placeholder="advisor-slug (optional)"
          onChange={(e) => setAdvisorScope(e.target.value)}
          onBlur={(e) => localStorage.setItem('lwp_advisor_slug', e.target.value)}
          data-testid="book-advisor-scope"
          style={{
            padding: '6px 10px',
            fontSize: 12,
            border: '1px solid var(--admin-border)',
            borderRadius: 6,
            background: 'var(--admin-surface)',
            color: 'var(--admin-slate)',
            width: 220,
          }}
        />
        <AdminThemeToggle />
        <button
          onClick={load}
          style={{ padding: '8px 14px', background: 'var(--admin-navy-solid)', color: 'var(--admin-ivory-solid)', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
          data-testid="book-refresh-btn"
        >
          Refresh
        </button>
      </Header>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
        <Stat label="Advisors in view" value={rollup.advisors} accent="var(--admin-navy-solid)" />
        <Stat label="Total clients" value={rollup.clients} accent="var(--admin-navy-solid)" />
        <Stat label="Active face amount" value={fmtMoney(rollup.face)} accent="var(--admin-gold-solid)" />
        <Stat label="Annualized premium" value={fmtMoney(rollup.premium)} accent="var(--admin-gold-solid)" />
      </div>

      {filtered && filtered.length === 0 ? (
        <Card title="No matching advisor">
          <div style={{ fontSize: 13, color: 'var(--admin-muted)' }} data-testid="book-no-match">
            No advisor found for slug <code>{advisorScope}</code>. Clear the scope to see all advisors.
          </div>
        </Card>
      ) : (
        (filtered || []).map((a) => <AdvisorSection key={a.advisor_slug} advisor={a} />)
      )}
    </div>
  )
}

const th = { textAlign: 'left', padding: 6, borderBottom: '1px solid var(--admin-border)', color: 'var(--admin-muted)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }
const thR = { ...th, textAlign: 'right' }
const td = { padding: 6, borderBottom: '1px solid var(--admin-border-soft)' }
const tdR = { ...td, textAlign: 'right' }
